import React, { useState, useEffect, useRef, useCallback } from "react";

import ChannelAddForm from "../../../../components/ChannelAddForm/ChannelAddForm";

import {
  StepChannelName,
  StepQrCode,
  StepPolling,
  StepResult,
} from "./steps";

import { addWhatsappWebChannel } from "../../../../../../services/api/channels/add/whatsapp-web";
import { ChannelCheck } from "../../../../../../services/api/channels/add/whatsapp-web-check";
import { ApiError } from "../../../../../../shared/utils/errors/ApiError";

const POLLING_INTERVAL = 10000;
const POLLING_TIMEOUT = 120;

export default function ChannelIntegrationQrCodePage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const [channelName, setChannelName] = useState("");
  const [nameError, setNameError] = useState("");

  const [qrcode, setQrcode] = useState("");
  const [instanceUUID, setInstanceUUID] = useState("");

  const [polling, setPolling] = useState(false);
  const [pollingTime, setPollingTime] = useState(0);

  const prevStepRef = useRef(currentStep);

  /* ------------------------------------------------------------------ */
  /* Criar canal + gerar QRCode                                         */
  /* ------------------------------------------------------------------ */
  const handleAdd = useCallback(async () => {
    if (!channelName.trim()) {
      setNameError("Digite um nome para o canal.");
      return;
    }

    setNameError("");

    try {
      setLoading(true);

      const response = await addWhatsappWebChannel({
        name: channelName,
      });

      if (response?.qrCode && response?.instance) {
        setQrcode(response.qrCode);
        setInstanceUUID(response.instance);
        setCurrentStep(2);
      } else {
        throw new Error("Resposta inválida ao gerar QRCode");
      }

    } catch (error) {
      console.error("Erro ao criar canal WhatsApp Web:", error);
      setSuccess(false);
      setCurrentStep(4);
    } finally {
      setLoading(false);
    }
  }, [channelName]);

  /* ------------------------------------------------------------------ */
  /* Detecta VOLTAR entre etapas                                        */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    const prevStep = prevStepRef.current;

    if (prevStep === 3 && currentStep === 2) {
      setPolling(false);
      setPollingTime(0);
      setQrcode("");
      setInstanceUUID("");

      handleAdd();
    }
 
    if ((prevStep === 2 || prevStep === 3) && currentStep === 1) {

      setPolling(false);
      setPollingTime(0);
      setQrcode("");
      setInstanceUUID("");
      setSuccess(false);
    }

    prevStepRef.current = currentStep;
  }, [currentStep, handleAdd]);

  /* ------------------------------------------------------------------ */
  /* Polling                                                            */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!polling || !instanceUUID) return;

    const interval = setInterval(async () => {
      await checkConnection();
      setPollingTime((prev) => prev + POLLING_INTERVAL / 1000);
    }, POLLING_INTERVAL);

    return () => clearInterval(interval);
  }, [polling, instanceUUID]);

  useEffect(() => {
    if (!polling) return;

    if (pollingTime >= POLLING_TIMEOUT) {
      console.warn("⏳ Tempo máximo atingido.");
      setPolling(false);
      setSuccess(false);
      setCurrentStep(4);
    }
  }, [pollingTime, polling]);

  /* ------------------------------------------------------------------ */
  /* Verifica conexão                                                   */
  /* ------------------------------------------------------------------ */
  const checkConnection = async () => {
    try {
      const result = await ChannelCheck({
        code: instanceUUID,
        name: channelName,
      });

      if (result?.id === instanceUUID) {
        setSuccess(true);
        setPolling(false);
        setCurrentStep(4);
      }
    } catch (error) {
      if (error instanceof ApiError) {
        if (
          error.error === "whatsapp_web_not_connected_yet" ||
          error.error === "whatsapp_web_pending"
        ) {
          return;
        }
      }

      console.error("Erro verificando conexão:", error);
      setSuccess(false);
      setPolling(false);
      setCurrentStep(4);
    }
  };

  /* ------------------------------------------------------------------ */
  /* Inicia polling                                                     */
  /* ------------------------------------------------------------------ */
  const startPolling = () => {
    if (!instanceUUID) return;

    setPollingTime(0);
    setPolling(true);
    setCurrentStep(3);
  };

  /* ------------------------------------------------------------------ */
  /* Reset completo                                                     */
  /* ------------------------------------------------------------------ */
  const resetForm = () => {
    setSuccess(false);
    setLoading(false);
    setCurrentStep(1);
    setChannelName("");
    setNameError("");
    setQrcode("");
    setInstanceUUID("");
    setPolling(false);
    setPollingTime(0);
  };

  /* ------------------------------------------------------------------ */
  /* Steps                                                              */
  /* ------------------------------------------------------------------ */
  const steps = [
    {
      title: "Vamos adicionar seu WhatsApp?",
      description: (
        <span>
          O primeiro passo é escolher um nome para o seu WhatsApp.
        </span>
      ),
      content: (
        <StepChannelName
          channelName={channelName}
          setChannelName={setChannelName}
          nameError={nameError}
        />
      ),
    },
    {
      title: "Leia o QR Code no seu WhatsApp",
      description: (
        <span>
          Em seu celular, acesse <strong>Configurações → Aparelhos conectados</strong>
        </span>
      ),
      content: <StepQrCode qrcode={qrcode} />,
    },
    {
      title: "Aguarde um instante 😉",
      description: <StepPolling pollingTime={pollingTime} />,
    },
    {
      title: success
        ? "WhatsApp conectado com sucesso!"
        : "Ops! Algo deu errado",
      description: <StepResult success={success} />,
    },
  ];

  return (
    <ChannelAddForm
      steps={steps}
      success={success}
      loading={loading}
      currentStep={currentStep}
      setCurrentStep={setCurrentStep}
      onSubmit={[handleAdd, startPolling]}
      onRetry={resetForm}
      disableBack={loading}
    />
  );
}
